import React from 'react';
import { AlertTriangle, RefreshCw, Home, RotateCcw } from 'lucide-react';
import logger from '../../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isChunkError: false
    };
    this.retryTimeout = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const isChunkError = error.name === 'ChunkLoadError' ||
                        error.message?.includes('Loading chunk') ||
                        error.message?.includes('Unexpected token');
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      isChunkError
    });
    
    logger.error('ErrorBoundary caught error', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      isChunkError
    });
    if (isChunkError) {
      logger.warn('Chunk loading error detected - build may be stale');
    }

    const apiUrl = typeof process !== 'undefined' && process.env?.REACT_APP_API_URL
      ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/logging/error`
      : '/api/logging/error';
    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack,
        context: { componentStack: errorInfo?.componentStack, isChunkError },
        level: 'error',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});

    // Auto-reload on chunk errors (stale build)
    if (isChunkError && this.state.retryCount === 0) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
    
    // Error tracking removed - Sentry was causing webpack issues
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    const { retryCount } = this.state;
    
    // Prevent infinite retry loops
    if (retryCount >= 3) {
      this.handleReload();
      return;
    }
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: retryCount + 1
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #0a0a0f 0%, #12121a 50%, #0a0a0f 100%)',
          fontFamily: "'Roboto Condensed', sans-serif",
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '500px',
            width: '100%',
            padding: '40px',
            background: 'rgba(20, 20, 25, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 107, 107, 0.3)',
            borderRadius: '20px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255, 107, 107, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <AlertTriangle size={40} style={{ color: '#FF6B6B' }} />
            </div>
            
            <h1 style={{
              margin: '0 0 12px',
              fontSize: '24px',
              fontWeight: 700,
              color: '#ffffff'
            }}>
              Something went wrong
            </h1>
            
            <p style={{
              margin: '0 0 24px',
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.6)',
              lineHeight: 1.6
            }}>
              {this.state.isChunkError
                ? 'The app needs to reload to get the latest version. Reloading automatically...'
                : 'The application encountered an unexpected error. Don\'t worry, your wallet and funds are safe.'}
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div style={{
                marginBottom: '24px',
                padding: '16px',
                background: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '12px',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                <p style={{
                  margin: '0 0 8px',
                  fontSize: '12px',
                  color: '#FF6B6B',
                  fontFamily: 'monospace'
                }}>
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre style={{
                    margin: 0,
                    fontSize: '10px',
                    color: 'rgba(255, 255, 255, 0.4)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {this.state.retryCount < 3 && (
                <button
                  onClick={this.handleRetry}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #9945FF 0%, #7B2CBF 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <RotateCcw size={18} />
                  Retry ({3 - this.state.retryCount} left)
                </button>
              )}
              
              <button
                onClick={this.handleReload}
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #7FFF00 0%, #c8b432 50%, #FF8C00 100%)',
                  backgroundSize: '200% 200%',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                <RefreshCw size={18} />
                Reload App
              </button>
              
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '14px 28px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                <Home size={18} />
                Go Home
              </button>
            </div>
            
            <p style={{
              margin: '24px 0 0',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.4)'
            }}>
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
